import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { App, Button, Card, Input, Space, Tag, Typography } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/he";
import { isAdminUser } from "../../../services/authService";
import { useRemindersHub } from "../../../features/reminders/context/RemindersHubContext";
import {
  fetchAdminMessagesFeed,
  createAdminMessage,
  seenAdminMessage,
  dismissAdminMessage,
  ackAdminMessage,
} from "../../../features/reminders/api/adminMessagesApi";
import MessageReadReportPanel from "../../../features/reminders/components/MessageReadReportPanel";
import "./RemindersPage.scss";

dayjs.extend(relativeTime);
dayjs.locale("he");

const ADMIN_BROADCAST_FE_TRACE =
  typeof import.meta !== "undefined" &&
  (Boolean(import.meta.env?.DEV) ||
    String(import.meta.env?.VITE_REMINDERS_BROADCAST_TRACE || "").trim() === "1");

const { Title, Text } = Typography;

const SYSTEM_BOARD_FILTER = {
  all: "all",
  unread: "unread",
  important: "important",
  ack: "ack",
  archive: "archive",
};

function displayTypeLabel(dt) {
  const d = (dt || "feed").toLowerCase();
  if (d === "popup") return "חלון קופץ";
  if (d === "banner") return "פס עליון";
  return "פיד";
}

function priorityLabel(p) {
  const x = (p || "normal").toLowerCase();
  if (x === "urgent") return "דחוף";
  if (x === "important") return "חשוב";
  return "רגיל";
}

function publisherLabel(email) {
  const e = (email || "").trim();
  if (!e) return "מערכת";
  const local = e.split("@")[0] || e;
  return local.length > 48 ? `${local.slice(0, 45)}…` : local;
}

function isAdminMessageExpired(m) {
  if (!m?.ends_at) return false;
  return dayjs(m.ends_at).isBefore(dayjs());
}

function passesSystemBoardFilter(m, filterId) {
  switch (filterId) {
    case SYSTEM_BOARD_FILTER.archive:
      return isAdminMessageExpired(m);
    case SYSTEM_BOARD_FILTER.unread:
      return !m.seen && !m.dismissed;
    case SYSTEM_BOARD_FILTER.important: {
      const p = (m.priority || "").toLowerCase();
      return p === "important" || p === "urgent";
    }
    case SYSTEM_BOARD_FILTER.ack:
      return Boolean(m.require_acknowledgement) && !m.acknowledged;
    default:
      return true;
  }
}

/** System messages board (personal notes / פתקים removed). */
export default function RemindersPage() {
  const { message } = App.useApp();
  const admin = isAdminUser();
  const { summary, refreshActiveAdmin, refreshSummary } = useRemindersHub();

  const [systemItems, setSystemItems] = useState([]);
  const [systemBoardFilter, setSystemBoardFilter] = useState(SYSTEM_BOARD_FILTER.all);
  const [systemLoading, setSystemLoading] = useState(false);
  const pendingScrollFirstUnreadSystemRef = useRef(false);
  const [systemUnreadScrollNonce, setSystemUnreadScrollNonce] = useState(0);

  const [adminForm, setAdminForm] = useState({
    title: "",
    body: "",
    image_url: "",
    cta_text: "",
    cta_link: "",
    audience_type: "all",
    audience_agent_ids_json: "[]",
    priority: "normal",
    display_type: "feed",
    show_once: false,
    require_acknowledgement: false,
    show_in_board: true,
    is_pinned: false,
  });

  const loadSystem = useCallback(async () => {
    setSystemLoading(true);
    try {
      const { data } = await fetchAdminMessagesFeed();
      setSystemItems(data?.items || []);
    } catch {
      setSystemItems([]);
      message.error("שגיאה בטעינת הודעות מערכת");
    } finally {
      setSystemLoading(false);
    }
  }, [message]);

  useEffect(() => {
    loadSystem();
  }, [loadSystem]);

  useEffect(() => {
    refreshSummary();
  }, [refreshSummary]);

  useEffect(() => {
    if (!pendingScrollFirstUnreadSystemRef.current) return;
    if (systemLoading) return;
    const firstUnread = (systemItems || []).find((m) => !m.seen && !m.dismissed);
    pendingScrollFirstUnreadSystemRef.current = false;
    if (!firstUnread) return;
    const id = `hr-sys-msg-${firstUnread.id}`;
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [systemLoading, systemItems, systemBoardFilter, systemUnreadScrollNonce]);

  const filteredSystemBoard = useMemo(() => {
    const list = systemItems || [];
    return list.filter((m) => passesSystemBoardFilter(m, systemBoardFilter));
  }, [systemItems, systemBoardFilter]);

  const submitAdmin = async () => {
    const title = (adminForm.title || "").trim();
    if (!title) {
      message.warning("כותרת נדרשת");
      return;
    }
    try {
      let audience_agent_ids;
      if (adminForm.audience_type === "groups") {
        try {
          audience_agent_ids = JSON.parse(adminForm.audience_agent_ids_json || "[]");
        } catch {
          message.error("מזהי קבוצה — JSON לא תקין");
          return;
        }
      }
      const { audience_agent_ids_json: _aj, ...rest } = adminForm;
      const payload = {
        ...rest,
        ...(audience_agent_ids != null ? { audience_agent_ids } : {}),
      };
      if (ADMIN_BROADCAST_FE_TRACE) {
        // eslint-disable-next-line no-console
        console.info("[ADMIN_BROADCAST_CREATE_TRACE]", {
          endpoint: "/api/reminders/admin-messages",
          method: "POST",
          payloadKeys: Object.keys(payload || {}),
        });
      }
      const res = await createAdminMessage(payload);
      if (ADMIN_BROADCAST_FE_TRACE) {
        // eslint-disable-next-line no-console
        console.info("[ADMIN_BROADCAST_CREATE_TRACE]", {
          ok: true,
          status: res?.status,
          itemId: res?.data?.item?.id ?? null,
        });
      }
      message.success("הודעת מערכת נוצרה");
      setAdminForm((f) => ({
        ...f,
        title: "",
        body: "",
        audience_agent_ids_json: "[]",
      }));
      await loadSystem();
      await refreshActiveAdmin();
      await refreshSummary();
    } catch (e) {
      const srv = e?.response?.data;
      const hint =
        (srv && typeof srv.message === "string" && srv.message) ||
        (srv && typeof srv.error === "string" && srv.error) ||
        (typeof e?.message === "string" && e.message) ||
        "";
      if (ADMIN_BROADCAST_FE_TRACE) {
        // eslint-disable-next-line no-console
        console.info("[ADMIN_BROADCAST_CREATE_TRACE]", {
          ok: false,
          status: e?.response?.status,
          body: e?.response?.data ?? null,
        });
      }
      message.error(hint ? `שמירה נכשלה — ${hint}` : "שמירה נכשלה");
    }
  };

  return (
    <div className="hr-reminders-page">
      <div className="hr-reminders-page__head">
        <div>
          <Title level={3} style={{ margin: 0 }}>
            הודעות מערכת
          </Title>
          <Text type="secondary">לוח הודעות פנימי מהנהלה</Text>
        </div>
        <Button
          onClick={() => {
            pendingScrollFirstUnreadSystemRef.current = true;
            setSystemBoardFilter(SYSTEM_BOARD_FILTER.unread);
            setSystemUnreadScrollNonce((n) => n + 1);
          }}
        >
          לא נקראו ({summary.admin_feed_unread ?? 0})
        </Button>
      </div>

      <div className="hr-sys-board">
        <Card title="לוח הודעות מערכת" loading={systemLoading} className="hr-sys-board__main-card">
          <Text type="secondary" className="hr-sys-board__intro">
            כל ההודעות שנשלחו אליך נשמרות כאן — גם אחרי סגירת חלון קופץ. ניתן לסנן לפי מצב קריאה
            וחשיבות.
          </Text>
          <div className="hr-sys-board__filters">
            {[
              { id: SYSTEM_BOARD_FILTER.all, label: "הכל" },
              { id: SYSTEM_BOARD_FILTER.unread, label: "לא נקראו" },
              { id: SYSTEM_BOARD_FILTER.important, label: "חשובות" },
              { id: SYSTEM_BOARD_FILTER.ack, label: "דורשות אישור" },
              { id: SYSTEM_BOARD_FILTER.archive, label: "ארכיון (פג תוקף)" },
            ].map(({ id, label }) => (
              <Button
                key={id}
                size="small"
                type={systemBoardFilter === id ? "primary" : "default"}
                onClick={() => setSystemBoardFilter(id)}
              >
                {label}
              </Button>
            ))}
          </div>
          {!systemLoading && filteredSystemBoard.length === 0 ? (
            <Text type="secondary">אין הודעות בתצוגה זו</Text>
          ) : null}
          <div className="hr-sys-board__grid">
            {filteredSystemBoard.map((m) => {
              const created = m.created_at ? dayjs(m.created_at) : null;
              const relative = created && created.isValid() ? created.fromNow() : null;
              const exact =
                created && created.isValid()
                  ? created.format("dddd D MMMM YYYY, HH:mm")
                  : "—";
              const expired = isAdminMessageExpired(m);
              const unread = !m.seen && !m.dismissed;
              const needAck = Boolean(m.require_acknowledgement) && !m.acknowledged;

              return (
                <Card
                  id={`hr-sys-msg-${m.id}`}
                  key={m.id}
                  size="small"
                  className={
                    "hr-sys-board__card" +
                    (m.is_pinned ? " hr-sys-board__card--pinned" : "") +
                    (expired ? " hr-sys-board__card--expired" : "")
                  }
                >
                  <div className="hr-sys-board__card-head">
                    <div className="hr-sys-board__title-row">
                      {m.is_pinned ? <Tag color="blue">נעוץ</Tag> : null}
                      {unread ? <Tag color="volcano">לא נקרא</Tag> : null}
                      {needAck ? <Tag color="magenta">דורש אישור</Tag> : null}
                      {expired ? <Tag>פג תוקף</Tag> : null}
                      <Tag>{priorityLabel(m.priority)}</Tag>
                      <Tag color="default">{displayTypeLabel(m.display_type)}</Tag>
                    </div>
                    <Title level={5} className="hr-sys-board__title">
                      {m.title}
                    </Title>
                  </div>
                  <div className="hr-sys-board__meta">
                    <Text type="secondary">
                      פורסם:{" "}
                      <strong
                        dir="ltr"
                        className="hr-sys-board__publisher-name"
                        title={m.created_by_email || undefined}
                      >
                        {publisherLabel(m.created_by_email)}
                      </strong>
                    </Text>
                    <div className="hr-sys-board__time">
                      <Text type="secondary">{exact}</Text>
                      {relative ? (
                        <Text type="secondary" className="hr-sys-board__time-rel">
                          · {relative}
                        </Text>
                      ) : null}
                    </div>
                  </div>
                  {m.image_url ? (
                    <div className="hr-sys-board__img-wrap">
                      <img src={m.image_url} alt="" className="hr-sys-board__img" />
                    </div>
                  ) : null}
                  {m.body ? <div className="hr-sys-board__body">{m.body}</div> : null}
                  {admin &&
                  Number.isFinite(Number(m.read_count)) &&
                  Number.isFinite(Number(m.unread_count)) ? (
                    <div className="hr-sys-board__read-summary">
                      נקראו: {Number(m.read_count)} | לא נקראו: {Number(m.unread_count)}
                    </div>
                  ) : null}
                  <Space wrap className="hr-sys-board__actions">
                    {m.cta_link && m.cta_text ? (
                      <Button
                        type="link"
                        href={m.cta_link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {m.cta_text}
                      </Button>
                    ) : null}
                    {!m.seen ? (
                      <Button
                        size="small"
                        type="primary"
                        ghost
                        onClick={async () => {
                          await seenAdminMessage(m.id);
                          await loadSystem();
                          await refreshActiveAdmin();
                          await refreshSummary();
                        }}
                      >
                        סמן נקרא
                      </Button>
                    ) : null}
                    {needAck ? (
                      <Button
                        size="small"
                        type="primary"
                        onClick={async () => {
                          await ackAdminMessage(m.id);
                          await loadSystem();
                          await refreshActiveAdmin();
                          await refreshSummary();
                        }}
                      >
                        אישור (הבנתי)
                      </Button>
                    ) : null}
                    <Button
                      size="small"
                      onClick={async () => {
                        await dismissAdminMessage(m.id);
                        await loadSystem();
                        await refreshActiveAdmin();
                        await refreshSummary();
                      }}
                    >
                      התעלם
                    </Button>
                  </Space>
                  {admin ? (
                    <div className="hr-sys-board__read-report-wrap">
                      <MessageReadReportPanel
                        messageId={m.id}
                        initialCounts={{
                          read_count: m.read_count,
                          unread_count: m.unread_count,
                          total_recipients: m.total_recipients,
                        }}
                      />
                    </div>
                  ) : null}
                </Card>
              );
            })}
          </div>
        </Card>
      </div>

      {admin ? (
        <Card title="יצירת הודעת אדמין (Broadcast)" className="hr-reminders-page__admin">
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Input
              placeholder="כותרת"
              value={adminForm.title}
              onChange={(e) => setAdminForm((f) => ({ ...f, title: e.target.value }))}
            />
            <Input.TextArea
              placeholder="תוכן"
              rows={3}
              value={adminForm.body}
              onChange={(e) => setAdminForm((f) => ({ ...f, body: e.target.value }))}
            />
            <Space wrap>
              <Input
                placeholder="image_url"
                value={adminForm.image_url}
                onChange={(e) => setAdminForm((f) => ({ ...f, image_url: e.target.value }))}
                style={{ minWidth: 220 }}
              />
              <Input
                placeholder="cta_text"
                value={adminForm.cta_text}
                onChange={(e) => setAdminForm((f) => ({ ...f, cta_text: e.target.value }))}
              />
              <Input
                placeholder="cta_link"
                value={adminForm.cta_link}
                onChange={(e) => setAdminForm((f) => ({ ...f, cta_link: e.target.value }))}
                style={{ minWidth: 240 }}
              />
            </Space>
            <Space wrap>
              <span>קהל:</span>
              {[
                { v: "all", l: "כולם" },
                { v: "agents", l: "סוכנים" },
                { v: "admins", l: "אדמינים" },
                { v: "groups", l: "קבוצות" },
              ].map(({ v, l }) => (
                <Button
                  key={v}
                  size="small"
                  type={adminForm.audience_type === v ? "primary" : "default"}
                  onClick={() => setAdminForm((f) => ({ ...f, audience_type: v }))}
                >
                  {l}
                </Button>
              ))}
            </Space>
            {adminForm.audience_type === "groups" ? (
              <Input.TextArea
                placeholder='מזהי סוכנים כ־JSON, למשל [1,2,3]'
                rows={2}
                value={adminForm.audience_agent_ids_json}
                onChange={(e) =>
                  setAdminForm((f) => ({ ...f, audience_agent_ids_json: e.target.value }))
                }
              />
            ) : null}
            <Space wrap>
              <span>עדיפות:</span>
              {[
                { v: "normal", l: "רגיל" },
                { v: "important", l: "חשוב" },
                { v: "urgent", l: "דחוף" },
              ].map(({ v, l }) => (
                <Button
                  key={v}
                  size="small"
                  type={adminForm.priority === v ? "primary" : "default"}
                  onClick={() => setAdminForm((f) => ({ ...f, priority: v }))}
                >
                  {l}
                </Button>
              ))}
            </Space>
            <Space wrap>
              <span>תצוגה:</span>
              {[
                { v: "feed", l: "פיד" },
                { v: "banner", l: "פס עליון" },
                { v: "popup", l: "חלון קופץ" },
              ].map(({ v, l }) => (
                <Button
                  key={v}
                  size="small"
                  type={adminForm.display_type === v ? "primary" : "default"}
                  onClick={() => setAdminForm((f) => ({ ...f, display_type: v }))}
                >
                  {l}
                </Button>
              ))}
            </Space>
            <Space>
              <Button
                size="small"
                type={adminForm.show_once ? "primary" : "default"}
                onClick={() => setAdminForm((f) => ({ ...f, show_once: !f.show_once }))}
              >
                הצג פעם אחת
              </Button>
              <Button
                size="small"
                type={adminForm.require_acknowledgement ? "primary" : "default"}
                onClick={() =>
                  setAdminForm((f) => ({
                    ...f,
                    require_acknowledgement: !f.require_acknowledgement,
                  }))
                }
              >
                דורש אישור
              </Button>
              <Button
                size="small"
                type={adminForm.show_in_board ? "primary" : "default"}
                onClick={() => setAdminForm((f) => ({ ...f, show_in_board: !f.show_in_board }))}
              >
                הופיע בלוח הקבוע
              </Button>
              <Button
                size="small"
                type={adminForm.is_pinned ? "primary" : "default"}
                onClick={() => setAdminForm((f) => ({ ...f, is_pinned: !f.is_pinned }))}
              >
                נעוץ בלוח
              </Button>
            </Space>
            <Button type="primary" onClick={submitAdmin}>
              פרסם הודעה
            </Button>
          </Space>
        </Card>
      ) : null}
    </div>
  );
}
